import { FC, ReactNode } from "react";
import styled from "@emotion/styled";
import { CSSObject } from "@emotion/react";
import Box from "./box";
import PFP from "../assets/pfp/placeholder.png";
import Heading from "./heading";
import Body from "./body";
import Stack from "./stack";
import Responsive from "./responsive";

type Props = {
  heading?: string;
  description?: string;
  pfp?: string;
  placeholder?: string;
  children?: ReactNode;
  localStyles?: CSSObject;
};

const El = styled(Box)<Props>(({ localStyles }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: "auto",
  width: "100%",
  minHeight: 500,
  textAlign: "center",

  padding: "calc(var(--scale-72) + var(--scale-24)) var(--scale-48)",
  justifyItems: "center",
  "@media (min-width: 1120px)": {
    padding: "calc(112px + var(--scale-24)) 0",
  },
  "@media (min-height: 1060px) and (min-width: 1600px)": {
    height: "100vh",
  },

  ...localStyles,
}));

const Content = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "var(--scale-24)",
  minHeight: 760,
});

const HeadingGroup = styled.div({
  display: "flex",
  width: "auto",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  gap: "var(--scale-36)",
});

const ButtonGroup = styled.div({
  display: "flex",
  width: "auto",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--scale-12)",
});

const Image = styled.img({
  width: "100%",
  height: "100%",
});

const Banner: FC<Props> = ({
  localStyles,
  pfp = PFP,
  placeholder,
  children,
  heading = "Title",
  description = "Description",
  ...props
}) => {
  const LogoImage: FC = () => {
    return (
      <Box localStyles={{ position: "relative", display: "flex" }}>
        <Image
          src={pfp}
          alt={heading}
          style={{ position: "relative", zIndex: 1 }}
          width="300"
          height="300"
        />
        <Image
          src={placeholder}
          alt={heading}
          style={{ position: "absolute" }}
          width="300"
          height="300"
        />
      </Box>
    );
  };

  return (
    <El localStyles={localStyles} {...props}>
      <Content>
        <Responsive above={600}>
          <Box
            localStyles={{
              width: 300,
              height: 300,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "100%",
              border: "8px solid white",
              boxShadow: "0 4px 0 0 rgba(0,0,0,0.3)",
              margin: "24px auto",
              overflow: "hidden",
            }}
          >
            <LogoImage />
          </Box>
        </Responsive>

        <Responsive below={599}>
          <Box
            localStyles={{
              width: 200,
              height: 200,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "100%",
              border: "5px solid white",
              boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)",
              margin: "24px auto",
              overflow: "hidden",
            }}
          >
            <LogoImage />
          </Box>
        </Responsive>

        <HeadingGroup>
          <Responsive below={1079}>
            <Stack
              direction="VERTICAL"
              space={"var(--scale-16)"}
              localStyles={{
                alignItems: "center",
                maxWidth: 480,
              }}
            >
              <Heading level="3">{heading}</Heading>
              <Body size="L" weight="BOLD" localStyles={{ maxWidth: "600px" }}>
                {description}
              </Body>
            </Stack>
          </Responsive>
          <Responsive above={1080}>
            <Stack
              direction="VERTICAL"
              space={"var(--scale-16)"}
              localStyles={{ alignItems: "center", marginBottom: 24 }}
            >
              <Heading level="1">{heading}</Heading>
              <Body size="XL" weight="BOLD" localStyles={{ maxWidth: "664px" }}>
                {description}
              </Body>
            </Stack>
          </Responsive>
          <ButtonGroup>{children}</ButtonGroup>
        </HeadingGroup>
      </Content>
    </El>
  );
};

export default Banner;
